/**
 * Availability.js - Set Weekly Availability
 * 
 * Allows teachers to set their availability for each weekday.
 * Supports multiple time slots per day (e.g., before school + after school).
 * Compact row layout for desktop, stacked for mobile.
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useIsMobile } from '../hooks/useIsMobile';
import Toast from '../components/Toast';
import dataStore from '../data/dataStore';

function Availability() {
  const { user, refreshUser } = useAuth();
  const { theme, colors, darkMode } = useTheme();
  const isMobile = useIsMobile();
  
  // New format: slots array per day
  const [availability, setAvailability] = useState({
    monday: { available: true, slots: [{ start: '06:30', end: '17:30' }] },
    tuesday: { available: true, slots: [{ start: '06:30', end: '17:30' }] },
    wednesday: { available: true, slots: [{ start: '06:30', end: '17:30' }] },
    thursday: { available: true, slots: [{ start: '06:30', end: '17:30' }] },
    friday: { available: true, slots: [{ start: '06:30', end: '17:30' }] }
  });
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
  const dayLabels = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

  // Load user availability on mount - handle both old and new format
  useEffect(() => {
    if (user) {
      const fullUser = dataStore.getUserById(user.id);
      if (fullUser?.availability) {
        // Convert old format to new format if needed
        const converted = {};
        days.forEach(day => {
          const dayData = fullUser.availability[day];
          if (dayData) {
            if (dayData.slots && Array.isArray(dayData.slots)) {
              // Already new format
              converted[day] = dayData;
            } else {
              // Old format - convert to slots array
              converted[day] = {
                available: dayData.available,
                slots: dayData.available && dayData.start && dayData.end 
                  ? [{ start: dayData.start, end: dayData.end }]
                  : [{ start: '06:30', end: '17:30' }]
              };
            }
          } else {
            converted[day] = { available: true, slots: [{ start: '06:30', end: '17:30' }] };
          }
        });
        setAvailability(converted);
      }
    }
  }, [user]);

  // Toggle availability for a day
  const toggleDay = (day) => {
    setAvailability(prev => ({
      ...prev,
      [day]: { ...prev[day], available: !prev[day].available }
    }));
  };

  // Update time for a specific slot
  const updateSlotTime = (day, slotIndex, field, value) => {
    setAvailability(prev => {
      const newSlots = [...prev[day].slots];
      newSlots[slotIndex] = { ...newSlots[slotIndex], [field]: value };
      return {
        ...prev,
        [day]: { ...prev[day], slots: newSlots }
      };
    });
  };

  // Add a new slot to a day
  const addSlot = (day) => {
    setAvailability(prev => {
      const lastSlot = prev[day].slots[prev[day].slots.length - 1];
      // Default new slot starts after last slot ends
      const newStart = lastSlot?.end || '12:00';
      return {
        ...prev,
        [day]: { 
          ...prev[day], 
          slots: [...prev[day].slots, { start: newStart, end: '17:30' }] 
        }
      };
    });
  };

  // Remove a slot from a day
  const removeSlot = (day, slotIndex) => {
    setAvailability(prev => {
      const newSlots = prev[day].slots.filter((_, i) => i !== slotIndex);
      // Ensure at least one slot remains
      if (newSlots.length === 0) {
        newSlots.push({ start: '06:30', end: '17:30' });
      }
      return {
        ...prev,
        [day]: { ...prev[day], slots: newSlots }
      };
    });
  };

  // Save availability
  const handleSave = async () => {
    try {
      await dataStore.updateUserAvailability(user.id, availability, user);
      refreshUser();
      setToast({ show: true, message: 'Availability saved!', type: 'success' });
    } catch (err) {
      console.error('Error saving availability:', err);
      setToast({ show: true, message: 'Error saving availability', type: 'error' });
    }
  };

  // Styles
  const containerStyle = {
    padding: isMobile ? '12px' : '20px',
    maxWidth: '900px',
    margin: '0 auto'
  };

  const cardStyle = {
    backgroundColor: theme.cardBackground,
    padding: isMobile ? '16px' : '30px',
    borderRadius: '8px',
    border: `1px solid ${theme.border}`
  };

  const titleStyle = {
    color: theme.text,
    marginTop: 0,
    marginBottom: '10px',
    fontSize: isMobile ? '1.3rem' : '1.5rem'
  };

  // Compact row style for each day
  const dayRowStyle = {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    alignItems: isMobile ? 'stretch' : 'center',
    gap: isMobile ? '10px' : '12px',
    padding: isMobile ? '14px' : '12px 16px',
    marginBottom: '8px',
    backgroundColor: theme.backgroundSecondary,
    borderRadius: '8px'
  };

  const dayLabelStyle = {
    width: isMobile ? '100%' : '90px',
    fontWeight: '600',
    color: theme.text,
    fontSize: '0.95rem',
    flexShrink: 0
  };

  const toggleButtonStyle = (isAvailable) => ({
    padding: isMobile ? '10px 16px' : '6px 14px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    backgroundColor: isAvailable ? colors.green : colors.red,
    color: darkMode ? '#1a1a2e' : '#fff',
    fontWeight: '600',
    fontSize: '0.85rem',
    flexShrink: 0,
    minWidth: isMobile ? '100%' : '110px'
  });

  const slotsContainerStyle = {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    alignItems: isMobile ? 'stretch' : 'center',
    gap: isMobile ? '8px' : '8px',
    flex: 1,
    flexWrap: 'wrap'
  };

  const slotStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: isMobile ? '8px 10px' : '4px 8px',
    backgroundColor: theme.cardBackground,
    borderRadius: '6px',
    border: `1px solid ${theme.border}`
  };

  const timeInputStyle = {
    padding: isMobile ? '8px' : '5px 6px',
    border: `1px solid ${theme.border}`,
    borderRadius: '4px',
    backgroundColor: theme.background,
    color: theme.text,
    width: isMobile ? '100%' : '95px',
    colorScheme: darkMode ? 'dark' : 'light',
    fontSize: isMobile ? '16px' : '0.85rem'
  };

  const removeButtonStyle = {
    padding: '4px 6px',
    backgroundColor: 'transparent',
    color: colors.red,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    lineHeight: 1,
    opacity: 0.7
  };

  const addButtonStyle = {
    padding: isMobile ? '10px 14px' : '6px 12px',
    backgroundColor: 'transparent',
    color: colors.blue,
    border: `1px dashed ${colors.blue}`,
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: '500',
    flexShrink: 0
  };

  const saveButtonStyle = {
    padding: isMobile ? '16px 30px' : '12px 30px',
    backgroundColor: colors.green,
    color: darkMode ? '#1a1a2e' : '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    cursor: 'pointer',
    marginTop: '20px',
    fontWeight: '600',
    width: isMobile ? '100%' : 'auto'
  };

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <h1 style={titleStyle}>📅 My Availability</h1>
        <p style={{ color: theme.textSecondary, marginBottom: '20px', fontSize: '0.9rem' }}>
          Set which days and hours you're available to work.
        </p>

        {days.map((day, index) => (
          <div key={day} style={dayRowStyle}>
            {/* Day Name */}
            <span style={dayLabelStyle}>{dayLabels[index]}</span>
            
            {/* Toggle Button */}
            <button
              style={toggleButtonStyle(availability[day].available)}
              onClick={() => toggleDay(day)}
            >
              {availability[day].available ? '✓ Available' : '✗ Unavailable'}
            </button>

            {/* Time Slots */}
            {availability[day].available && (
              <div style={slotsContainerStyle}>
                {availability[day].slots.map((slot, slotIndex) => (
                  <div key={slotIndex} style={slotStyle}>
                    <input
                      type="time"
                      value={slot.start}
                      onChange={(e) => updateSlotTime(day, slotIndex, 'start', e.target.value)}
                      style={timeInputStyle}
                    />
                    <span style={{ color: theme.textSecondary, fontSize: '0.85rem' }}>–</span>
                    <input
                      type="time"
                      value={slot.end}
                      onChange={(e) => updateSlotTime(day, slotIndex, 'end', e.target.value)}
                      style={timeInputStyle}
                    />
                    {availability[day].slots.length > 1 && (
                      <button
                        style={removeButtonStyle}
                        onClick={() => removeSlot(day, slotIndex)}
                        title="Remove slot"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
                
                {/* Add Slot Button */}
                <button
                  style={addButtonStyle}
                  onClick={() => addSlot(day)}
                >
                  + Add
                </button>
              </div>
            )}
          </div>
        ))}

        <button style={saveButtonStyle} onClick={handleSave}>
          Save Availability
        </button>
      </div>

      {/* Toast notification */}
      {toast.show && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast({ ...toast, show: false })} 
        />
      )}
    </div>
  );
}

export default Availability;
